import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Region } from '../../shared/types'

export type Lang = 'en' | 'pt'

const KEY = 'padel-madeira:lang'

export const LOCALE: Record<Lang, string> = { en: 'en-GB', pt: 'pt-PT' }

interface Strings {
  tagline: string
  themeToLight: string
  themeToDark: string
  language: string

  selectAll: string
  clearAll: string
  clubCount: (shown: number, total: number) => string
  legend: string
  mapLabel: string
  toggleClub: (name: string) => string
  region: Record<Region, string>

  nearMe: string
  locating: string
  within: string
  clubs: (n: number) => string
  clear: string
  locationBlocked: string
  locationUnavailable: string
  distanceLabel: string
  placePlaceholder: string
  placeLabel: string
  orPickPlace: string
  fromPlace: (name: string) => string

  thisWeek: string
  nextWeek: string
  today: string
  prevWeek: string
  nextWeekLabel: string
  updated: string
  loading: string

  playFor: string

  gridLabel: string
  notOpenYet: string
  notOpenYetNote: (through: string) => string
  cellFree: (date: string, time: string, courts: number, clubs: string) => string
  cellEmpty: (date: string, time: string) => string

  from: string
  courtAtClub: string
  free: string
  priceOnSite: string
  bookOn: (platform: string) => string
  contactClub: string
  moreClubs: (n: number) => string
  close: string

  noClubs: string
  nothingFree: (duration: string) => string
  loadError: string
  sourceDown: (source: string, error: string, clubs: number) => string

  footer: string
  contactPrompt: string
}

const EN: Strings = {
  tagline: 'Every available court, in one calendar',
  themeToLight: 'Switch to light theme',
  themeToDark: 'Switch to dark theme',
  language: 'Language',

  selectAll: 'Select all',
  clearAll: 'Clear all',
  clubCount: (shown, total) => `${shown} of ${total} clubs`,
  legend: 'no availability online - call the club to book.',
  mapLabel: 'Map of padel clubs on Madeira. Tap a pin to include or exclude a club.',
  toggleClub: (name) => `Toggle ${name}`,
  region: {
    Funchal: 'Funchal',
    West: 'West',
    East: 'East',
    North: 'North',
    'Porto Santo': 'Porto Santo',
  },

  nearMe: 'Near me',
  locating: 'Finding you...',
  within: 'Within',
  clubs: (n) => `${n} club${n === 1 ? '' : 's'}`,
  clear: 'clear',
  locationBlocked: 'Location blocked. Allow it in your browser settings, or pick clubs by hand.',
  locationUnavailable: 'Could not get your location. Pick clubs by hand instead.',
  distanceLabel: 'Distance from you',
  placePlaceholder: 'Type a town in Madeira...',
  placeLabel: 'Choose a place in Madeira',
  orPickPlace: 'or pick a place',
  fromPlace: (name) => `from ${name}`,

  thisWeek: 'This week',
  nextWeek: 'Next week',
  today: 'Today',
  prevWeek: 'Previous week',
  nextWeekLabel: 'Next week',
  updated: 'Updated',
  loading: 'Loading',

  playFor: 'Play for',

  gridLabel: 'Weekly padel court availability',
  notOpenYet: 'not open yet',
  notOpenYetNote: (through) =>
    `Clubs open bookings on a rolling window. The selected clubs publish up to ${through}; later days are not bookable yet, not fully booked.`,
  cellFree: (date, time, courts, clubs) =>
    `${date} ${time}, ${courts} court${courts > 1 ? 's' : ''} free at ${clubs}`,
  cellEmpty: (date, time) => `${date} ${time}, nothing free`,

  from: 'from',
  courtAtClub: 'Court assigned at the club',
  free: 'Free',
  priceOnSite: 'Price on site',
  bookOn: (platform) => `Book on ${platform}`,
  contactClub: 'Contact the club',
  moreClubs: (n) => `+${n} more club${n === 1 ? '' : 's'} free at this time`,
  close: 'Close',

  noClubs: 'No clubs selected - tap a pin or a chip to include one.',
  nothingFree: (duration) => `Nothing free for ${duration} this week at the selected clubs.`,
  loadError: 'Could not load availability',
  sourceDown: (source, error, clubs) =>
    `${source} did not respond${error ? ` (${error})` : ''} - ${clubs} club${clubs === 1 ? '' : 's'} may show as empty.`,

  contactPrompt: 'Something wrong or missing?',
  footer:
    "Times shown in Madeira local time. Prices are the whole-court rate; some platforms add a booking fee at checkout. Availability is read from each club's own booking system and can be a few minutes stale - the club's site is always the source of truth.",
}

const PT: Strings = {
  tagline: 'Todos os campos disponíveis, num só calendário',
  themeToLight: 'Mudar para tema claro',
  themeToDark: 'Mudar para tema escuro',
  language: 'Idioma',

  selectAll: 'Selecionar todos',
  clearAll: 'Limpar',
  clubCount: (shown, total) => `${shown} de ${total} clubes`,
  legend: 'sem disponibilidade online - ligue ao clube para reservar.',
  mapLabel: 'Mapa dos clubes de padel da Madeira. Toque num pino para incluir ou excluir um clube.',
  toggleClub: (name) => `Alternar ${name}`,
  region: {
    Funchal: 'Funchal',
    West: 'Oeste',
    East: 'Leste',
    North: 'Norte',
    'Porto Santo': 'Porto Santo',
  },

  nearMe: 'Perto de mim',
  locating: 'A localizar...',
  within: 'Até',
  clubs: (n) => `${n} clube${n === 1 ? '' : 's'}`,
  clear: 'limpar',
  locationBlocked:
    'Localização bloqueada. Permita-a nas definições do navegador, ou escolha os clubes à mão.',
  locationUnavailable: 'Não foi possível obter a sua localização. Escolha os clubes à mão.',
  distanceLabel: 'Distância a si',
  placePlaceholder: 'Escreva uma localidade da Madeira...',
  placeLabel: 'Escolha uma localidade da Madeira',
  orPickPlace: 'ou escolha uma localidade',
  fromPlace: (name) => `a partir de ${name}`,

  thisWeek: 'Esta semana',
  nextWeek: 'Próxima semana',
  today: 'Hoje',
  prevWeek: 'Semana anterior',
  nextWeekLabel: 'Semana seguinte',
  updated: 'Atualizado',
  loading: 'A carregar',

  playFor: 'Jogar durante',

  gridLabel: 'Disponibilidade semanal dos campos de padel',
  notOpenYet: 'ainda fechado',
  notOpenYetNote: (through) =>
    `Os clubes abrem as reservas numa janela deslizante. Os clubes selecionados publicam até ${through}; os dias seguintes ainda não estão disponíveis, não estão esgotados.`,
  cellFree: (date, time, courts, clubs) =>
    `${date} ${time}, ${courts} campo${courts > 1 ? 's' : ''} livre${courts > 1 ? 's' : ''} em ${clubs}`,
  cellEmpty: (date, time) => `${date} ${time}, nada livre`,

  from: 'a partir das',
  courtAtClub: 'Campo atribuído no clube',
  free: 'Grátis',
  priceOnSite: 'Preço no site',
  bookOn: (platform) => `Reservar em ${platform}`,
  contactClub: 'Contactar o clube',
  moreClubs: (n) => `+${n} clube${n === 1 ? '' : 's'} livre${n === 1 ? '' : 's'} a esta hora`,
  close: 'Fechar',

  noClubs: 'Nenhum clube selecionado - toque num pino ou numa etiqueta para incluir.',
  nothingFree: (duration) => `Nada livre para ${duration} esta semana nos clubes selecionados.`,
  loadError: 'Não foi possível carregar a disponibilidade',
  sourceDown: (source, error, clubs) =>
    `${source} não respondeu${error ? ` (${error})` : ''} - ${clubs} clube${clubs === 1 ? '' : 's'} pode${clubs === 1 ? '' : 'm'} aparecer vazio${clubs === 1 ? '' : 's'}.`,

  contactPrompt: 'Algo errado ou em falta?',
  footer:
    'Horas na hora local da Madeira. Os preços são do campo inteiro; algumas plataformas acrescentam uma taxa de reserva no pagamento. A disponibilidade é lida do sistema de reservas de cada clube e pode estar alguns minutos desatualizada - o site do clube é sempre a fonte definitiva.',
}

const DICT: Record<Lang, Strings> = { en: EN, pt: PT }

interface Ctx {
  lang: Lang
  setLang: (next: Lang) => void
  t: Strings
  locale: string
}

const I18nContext = createContext<Ctx | null>(null)

function initial(): Lang {
  const stored = localStorage.getItem(KEY)
  return stored === 'pt' ? 'pt' : 'en'
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initial)

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem(KEY, next)
    setLangState(next)
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo(
    () => ({ lang, setLang, t: DICT[lang], locale: LOCALE[lang] }),
    [lang, setLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider')
  return ctx
}
